import { useCallback, useEffect, useState } from 'react';
import {
  App,
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
} from 'antd';
import { PlusOutlined, ReloadOutlined, SearchOutlined, UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { api, formatBytes, ImageItem } from '../../services/api';
import { ResourceURL } from '../../components/ResourceURL';
import { ResourceID } from '../../components/ResourceID';
import { ImagePreview } from '../../components/MediaPreview';
import '../media.css';

export default function ImagesPage() {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<ImageItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [keyword, setKeyword] = useState('');
  const [format, setFormat] = useState<string>();
  const [status, setStatus] = useState('all');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ImageItem | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [fileList, setFileList] = useState<any[]>([]);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.images.list({
        page,
        page_size: pageSize,
        keyword: keyword || undefined,
        format: format || undefined,
        status,
      });
      setItems(res.items || []);
      setTotal(res.total || 0);
    } catch (e) {
      message.error(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, keyword, format, status, message]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ status: 'active' });
    setFileList([]);
    setOpen(true);
  };

  const openEdit = (row: ImageItem) => {
    setEditing(row);
    form.setFieldsValue(row);
    setFileList([]);
    setOpen(true);
  };

  const onSubmit = async () => {
    const values = await form.validateFields();
    setSubmitting(true);
    try {
      if (editing) {
        if (fileList[0]?.originFileObj) {
          const fd = new FormData();
          Object.entries(values).forEach(([k, v]) => {
            if (v !== undefined && v !== null) fd.append(k, String(v));
          });
          fd.append('file', fileList[0].originFileObj);
          await api.images.update(editing.id, fd);
        } else {
          await api.images.update(editing.id, values);
        }
        message.success('已更新');
      } else {
        if (!fileList[0]?.originFileObj) {
          message.warning('请上传图片文件');
          setSubmitting(false);
          return;
        }
        const fd = new FormData();
        Object.entries(values).forEach(([k, v]) => {
          if (v !== undefined && v !== null) fd.append(k, String(v));
        });
        fd.append('file', fileList[0].originFileObj);
        await api.images.create(fd);
        message.success('已添加');
      }
      setOpen(false);
      load();
    } catch (e) {
      message.error(e instanceof Error ? e.message : '保存失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="media-head">
        <div>
          <Typography.Title level={3} className="tech-title" style={{ margin: 0 }}>
            图片管理
          </Typography.Title>
          <Typography.Text type="secondary">宽高、格式等图片专属属性</Typography.Text>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load}>
            刷新
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            添加图片
          </Button>
        </Space>
      </div>

      <div className="tech-panel media-toolbar">
        <Space wrap>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="搜索名称 / 描述 / 标签"
            style={{ width: 240 }}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onPressEnter={() => {
              setPage(1);
              load();
            }}
          />
          <Select
            allowClear
            placeholder="格式"
            style={{ width: 120 }}
            value={format}
            onChange={setFormat}
            options={['jpeg', 'png', 'webp', 'gif'].map((v) => ({ value: v, label: v }))}
          />
          <Select
            style={{ width: 120 }}
            value={status}
            onChange={setStatus}
            options={[
              { value: 'all', label: '全部状态' },
              { value: 'active', label: '启用' },
              { value: 'disabled', label: '停用' },
            ]}
          />
          <Button
            type="primary"
            ghost
            onClick={() => {
              setPage(1);
              load();
            }}
          >
            查询
          </Button>
        </Space>
      </div>

      <div className="tech-panel media-table">
        <Table
          rowKey="id"
          loading={loading}
          dataSource={items}
          scroll={{ x: 1560 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (t) => `共 ${t} 条`,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
          columns={[
            {
              title: '预览',
              width: 88,
              render: (_: unknown, row: ImageItem) => (
                <ImagePreview url={row.file_url} name={row.name} />
              ),
            },
            {
              title: '资源 ID',
              dataIndex: 'resource_id',
              width: 280,
              render: (v: string) => <ResourceID id={v} />,
            },
            {
              title: '名称',
              dataIndex: 'name',
              render: (v: string, row: ImageItem) => (
                <div>
                  <div className="media-name">{v}</div>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {row.format || row.mime_type} · {formatBytes(row.file_size)} · {row.width}×{row.height}
                  </Typography.Text>
                </div>
              ),
            },
            {
              title: 'URL',
              dataIndex: 'file_url',
              width: 300,
              render: (v: string) => <ResourceURL url={v} />,
            },
            {
              title: '标签',
              dataIndex: 'tags',
              ellipsis: true,
              render: (v: string) =>
                v
                  ? v.split(',').map((t) => (
                      <Tag key={t} style={{ marginBottom: 2 }}>
                        {t}
                      </Tag>
                    ))
                  : '-',
            },
            {
              title: '状态',
              dataIndex: 'status',
              width: 90,
              render: (v: string) => (
                <Tag color={v === 'active' ? 'success' : 'default'}>{v === 'active' ? '启用' : '停用'}</Tag>
              ),
            },
            {
              title: '更新',
              dataIndex: 'updated_at',
              width: 160,
              render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm'),
            },
            {
              title: '操作',
              width: 150,
              render: (_: unknown, row: ImageItem) => (
                <Space>
                  <Button type="link" onClick={() => openEdit(row)}>
                    编辑
                  </Button>
                  <Popconfirm
                    title="确认删除？"
                    onConfirm={async () => {
                      try {
                        await api.images.remove(row.id);
                        message.success('已删除');
                        load();
                      } catch (e) {
                        message.error(e instanceof Error ? e.message : '删除失败');
                      }
                    }}
                  >
                    <Button type="link" danger>
                      删除
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </div>

      <Modal
        title={editing ? '编辑图片' : '添加图片'}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={onSubmit}
        confirmLoading={submitting}
        width={560}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item name="name" label="名称" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="描述">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="tags" label="标签">
            <Input placeholder="逗号分隔，如：轻松,欢快" />
          </Form.Item>
          <Form.Item name="status" label="状态" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'active', label: '启用' },
                { value: 'disabled', label: '停用' },
              ]}
            />
          </Form.Item>
          <Form.Item label={editing ? '替换文件（可选）' : '上传文件'} required={!editing}>
            <Upload
              beforeUpload={() => false}
              maxCount={1}
              accept="image/*"
              fileList={fileList}
              onChange={({ fileList: fl }) => setFileList(fl)}
            >
              <Button icon={<UploadOutlined />}>选择图片</Button>
            </Upload>
          </Form.Item>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            格式与分辨率将由系统自动识别，无需手工填写。
          </Typography.Text>
        </Form>
      </Modal>
    </div>
  );
}
